/**
 * Cart Drawer Utility
 * 
 * Handles cart drawer functionality using Shopify Cart API
 */

import { gsap } from "gsap";
import cart from './cart.js';

export class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = this.drawer?.querySelector('.cart-drawer__overlay');
    this.drawerContainer = this.drawer?.querySelector('.cart-drawer__container');
    this.closeButton = this.drawer?.querySelector('.cart-drawer__close');
    this.itemsContainer = this.drawer?.querySelector('.cart-drawer__items');
    this.footer = this.drawer?.querySelector('.cart-drawer__footer');
    this.subtotalElement = this.drawer?.querySelector('[data-cart-subtotal]');
    this.totalElement = this.drawer?.querySelector('[data-cart-total]');
    this.freeShipping = this.drawer?.querySelector('[data-free-shipping]');
    this.freeShippingMessage = this.drawer?.querySelector('[data-free-shipping-message]');
    this.isOpen = false;
    this.listenersAttached = false; // Track if listeners are attached to server-rendered items

    if (this.drawer) {
      this.init();
      this.events();
      // Store instance globally for product cards to access
      window.cartDrawerInstance = this;

      // Native /cart/add lands on the cart template. Open the drawer so that
      // page is not a dead end (this theme has no standalone cart UI).
      if (document.body.classList.contains('template-cart')) {
        this.open();
      }
    }
  }

  init = () => {
    // Set initial state - drawer slides from right (same pattern as search modal)
    if (this.drawerContainer) {
      gsap.set(this.drawerContainer, { x: '100%', opacity: 1 });
    }
    if (this.overlay) {
      gsap.set(this.overlay, { opacity: 0 });
    }
    this.drawer.style.visibility = 'hidden';
    this.drawer.style.pointerEvents = 'none';
  }

  events = () => {
    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.close);
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', this.close);
    }

    // Empty-state "Keep shopping" closes the drawer
    if (this.itemsContainer) {
      this.itemsContainer.addEventListener('click', (e) => {
        if (e.target.closest('.keep-shopping')) {
          this.close();
        }
      });
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Listen for cart updates (only refresh when adding new items, not on drawer changes)
    document.addEventListener('cart:add', () => this.refreshCart());
  }

  async refreshCart() {
    try {
      const cartData = await cart.get();
      this.renderCart(cartData);
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  // Escape cart-data strings before interpolating them into innerHTML —
  // line-item properties are client-supplied via the Cart API.
  escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  setFooterVisibility(hasItems) {
    if (this.footer) {
      this.footer.hidden = !hasItems;
    }
    this.drawer?.classList.toggle('is-empty', !hasItems);
  }

  getEmptyStateHTML() {
    return `
      <p class="cart-drawer__empty">Your cart is empty</p>
      <button type="button" class="keep-shopping">
        <span class="f-h text-uppercase color-white">Keep Shopping</span>
      </button>
    `;
  }

  formatMoney(cents, currency = 'EUR') {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency
    }).format((cents || 0) / 100);
  }

  updateFooter(cartData) {
    if (!cartData || cartData.total_price === undefined) return;

    const currency = cartData.currency || 'EUR';
    const totalPrice = cartData.total_price;
    const subtotalPrice = cartData.items_subtotal_price !== undefined
      ? cartData.items_subtotal_price
      : totalPrice;

    if (this.subtotalElement) {
      this.subtotalElement.textContent = this.formatMoney(subtotalPrice, currency);
    }
    if (this.totalElement) {
      this.totalElement.textContent = this.formatMoney(totalPrice, currency);
    }

    this.updateFreeShipping(totalPrice, currency);
  }

  updateFreeShipping(totalPrice, currency = 'EUR') {
    if (!this.freeShipping) return;

    // Rendered in cents by layout/theme.liquid from the shop metafield
    // shipping.free_thresholds. A threshold of 0 means the country ships free
    // whatever the cart is worth: the server already rendered the bar as
    // complete, and there is nothing to recompute. No threshold, same thing.
    const threshold = parseInt(this.freeShipping.dataset.threshold, 10);
    if (!Number.isFinite(threshold) || threshold <= 0) return;

    const remaining = Math.max(0, threshold - (totalPrice || 0));
    const progress = Math.min(1, (totalPrice || 0) / threshold);
    const qualified = remaining === 0;

    this.freeShipping.classList.toggle('is-complete', qualified);
    this.freeShipping.style.setProperty('--free-shipping-progress', String(progress));

    if (this.freeShippingMessage) {
      if (qualified) {
        this.freeShippingMessage.textContent = this.freeShipping.dataset.qualifiedMessage || '';
      } else {
        const template = this.freeShipping.dataset.remainingTemplate || '';
        const remainingAmount = `${Math.ceil(remaining / 100)}€`;
        this.freeShippingMessage.textContent = template.replace('__AMOUNT__', remainingAmount);
      }
    }
  }

  renderCart(cartData) {
    if (!this.itemsContainer) return;

    const hasItems = Boolean(cartData.items && cartData.items.length > 0);
    this.setFooterVisibility(hasItems);
    this.updateFooter(cartData);

    // Re-render items using JavaScript - matching Liquid structure
    if (hasItems) {
      const itemsHTML = cartData.items.map((item, index) => {
        // A hidden "_frame_image" property (set when adding a framed poster)
        // overrides the generic Frame product image with the selected color.
        // Line-item properties are client-supplied, so only trust http(s)
        // URLs served from Shopify's CDN — image_url generates them
        // protocol-relative on the shop's domain (//shop.tld/cdn/...).
        let frameImage = '';
        const rawFrameImage = item.properties && item.properties._frame_image;
        if (typeof rawFrameImage === 'string' && rawFrameImage) {
          try {
            const parsed = new URL(rawFrameImage, window.location.origin);
            const isHttp = parsed.protocol === 'https:' || parsed.protocol === 'http:';
            const isTrusted = isHttp && (
              parsed.hostname === 'cdn.shopify.com'
              || ((parsed.hostname.endsWith('.myshopify.com') || parsed.hostname === window.location.hostname)
                && parsed.pathname.startsWith('/cdn/'))
            );
            if (isTrusted) frameImage = rawFrameImage;
          } catch {
            // Malformed URL — ignore and fall back to the variant image.
          }
        }
        const imageUrl = frameImage
          || (item.image ? `${item.image}?width=200&height=200` : '');

        const formattedPrice = item.final_line_price
          ? this.formatMoney(item.final_line_price, cartData.currency || 'EUR')
          : '';

        // Shopify cart line numbers are 1-indexed
        const lineNumber = item.line || (index + 1);

        // Build variant display with option names (matching Liquid structure)
        let variantHTML = '';
        if (item.options_with_values && item.options_with_values.length > 0 && item.variant_title !== 'Default Title') {
          const variantOptions = item.options_with_values
            .map(option => `<span class="cart-drawer__variant-option">${this.escapeHtml(option.name)}: ${this.escapeHtml(option.value)}</span>`)
            .join('<span class="cart-drawer__variant-separator"> / </span>');
          variantHTML = `<div class="cart-drawer__item-variant">${variantOptions}</div>`;
        }

        // Build properties display (e.g., Frame color)
        let propertiesHTML = '';
        if (item.properties) {
          const props = Object.entries(item.properties)
            .filter(([key, value]) => value && !key.startsWith('_') && !String(value).startsWith('_'))
            .map(([key, value]) => `<span class="cart-drawer__item-property">${this.escapeHtml(key)}: ${this.escapeHtml(value)}</span>`)
            .join('');
          if (props) {
            propertiesHTML = `<div class="cart-drawer__item-properties">${props}</div>`;
          }
        }

        return `
          <div class="cart-drawer__item" data-line-item="${item.key}" data-line-number="${lineNumber}">
            ${imageUrl ? `
              <img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(item.title)}" class="cart-drawer__item-image" loading="lazy">
            ` : ''}
            <div class="cart-drawer__item-content">
              <h3 class="cart-drawer__item-title f-h7 f-regular">${this.escapeHtml(item.product_title)}</h3>
              <div class="d-flex">
                ${variantHTML}
                <span class="cart-drawer__item-price">${formattedPrice}</span>
              </div>
              ${propertiesHTML}
              <div class="cart-drawer__item-footer">
                <div class="cart-drawer__item-quantity">
                  <button type="button" class="cart-drawer__quantity-btn" data-action="decrease" data-line="${lineNumber}" aria-label="Decrease quantity">
                    <svg width="7" height="1" viewBox="0 0 7 1" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 0.5H7" stroke="#323232"/>
                    </svg>
                  </button>
                  <input type="number" class="cart-drawer__quantity-value" value="${item.quantity}" min="1" inputmode="numeric" data-line="${lineNumber}" aria-label="Quantity">
                  <button type="button" class="cart-drawer__quantity-btn" data-action="increase" data-line="${lineNumber}" aria-label="Increase quantity">
                    <svg width="7" height="7" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 3.5H7M3.5 0L3.5 7" stroke="#323232"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <button type="button" class="cart-drawer__item-remove" data-line="${lineNumber}" aria-label="Remove ${this.escapeHtml(item.product_title)}">
              <span class="">Remove</span>
            </button>
          </div>
        `;
      }).join('');

      this.itemsContainer.innerHTML = itemsHTML;

      // Attach event listeners to quantity buttons
      this.attachItemListeners();
    } else {
      this.itemsContainer.innerHTML = this.getEmptyStateHTML();
    }
  }

  /**
   * Update a line's quantity via the Cart API and sync the item's quantity
   * input, line price, and cart total in place (no full re-render).
   * Shared by the -/+ buttons and direct edits of the quantity input.
   */
  async changeLineQuantity(itemElement, lineNumber, newQuantity) {
    const quantityElement = itemElement.querySelector('.cart-drawer__quantity-value');

    // Disable the line's controls during the update
    const controls = itemElement.querySelectorAll('button, .cart-drawer__quantity-value');
    controls.forEach(control => control.disabled = true);

    try {
      // Update quantity using line number (1-indexed)
      const updatedCart = await cart.change(lineNumber, newQuantity);

      if (updatedCart.total_price === undefined) {
        await this.refreshCart();
        return;
      }

      // Update only the quantity display (no full re-render)
      if (quantityElement) quantityElement.value = newQuantity;

      // Update the item's line price
      const priceElement = itemElement.querySelector('.cart-drawer__item-price');
      if (priceElement && updatedCart.items) {
        const updatedItem = updatedCart.items.find(i => (i.line || (updatedCart.items.indexOf(i) + 1)) === lineNumber);
        if (updatedItem) {
          priceElement.textContent = this.formatMoney(
            updatedItem.final_line_price,
            updatedCart.currency || 'EUR'
          );
        }
      }

      this.updateFooter(updatedCart);

      // Dispatch event to update cart count
      document.dispatchEvent(new CustomEvent('cart:change'));
    } catch (error) {
      console.error('Error updating cart quantity:', error);
      // Only refresh on error
      await this.refreshCart();
    } finally {
      controls.forEach(control => control.disabled = false);
    }
  }

  attachItemListeners() {
    // Quantity buttons
    const quantityButtons = this.itemsContainer.querySelectorAll('.cart-drawer__quantity-btn');
    quantityButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const action = e.currentTarget.getAttribute('data-action');
        const lineNumber = parseInt(e.currentTarget.getAttribute('data-line'), 10);
        const itemElement = e.currentTarget.closest('.cart-drawer__item');
        const quantityElement = itemElement?.querySelector('.cart-drawer__quantity-value');

        if (!quantityElement || !lineNumber) {
          console.error('Missing line number or quantity element');
          return;
        }

        const currentQuantity = parseInt(quantityElement.value, 10) || 1;
        let newQuantity = currentQuantity;

        if (action === 'increase') {
          newQuantity = currentQuantity + 1;
        } else if (action === 'decrease') {
          newQuantity = Math.max(1, currentQuantity - 1);
        }

        await this.changeLineQuantity(itemElement, lineNumber, newQuantity);
      });
    });

    // Direct edits of the quantity input (typed value commits on change/Enter)
    const quantityInputs = this.itemsContainer.querySelectorAll('.cart-drawer__quantity-value');
    quantityInputs.forEach(input => {
      // Remember the last synced value so no-op edits skip the API call
      input.addEventListener('focus', (e) => {
        e.currentTarget.dataset.previousValue = e.currentTarget.value;
        e.currentTarget.select();
      });

      // Enter commits the value (blur fires the change event)
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      });

      input.addEventListener('change', async (e) => {
        const inputElement = e.currentTarget;
        const lineNumber = parseInt(inputElement.getAttribute('data-line'), 10);
        const itemElement = inputElement.closest('.cart-drawer__item');

        if (!itemElement || !lineNumber) {
          console.error('Missing line number or item element');
          return;
        }

        const previousQuantity = parseInt(inputElement.dataset.previousValue, 10) || 1;
        const newQuantity = parseInt(inputElement.value, 10);

        // Empty/invalid/unchanged input → restore the last synced value
        if (!newQuantity || newQuantity < 1 || newQuantity === previousQuantity) {
          inputElement.value = previousQuantity;
          return;
        }

        await this.changeLineQuantity(itemElement, lineNumber, newQuantity);
        inputElement.dataset.previousValue = inputElement.value;
      });
    });

    // Remove buttons
    const removeButtons = this.itemsContainer.querySelectorAll('.cart-drawer__item-remove');
    removeButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const lineNumber = parseInt(e.currentTarget.getAttribute('data-line'), 10);

        if (!lineNumber) {
          console.error('Missing line number');
          return;
        }

        // Disable button during removal
        button.disabled = true;

        try {
          // Remove item by setting quantity to 0
          const updatedCart = await cart.change(lineNumber, 0);

          // Remove the item element from DOM (smooth removal)
          const itemElement = button.closest('.cart-drawer__item');
          if (itemElement) {

            gsap.to(itemElement, {
              height: 0, padding: 0, duration: 1, ease: 'expo.out', onComplete: () => {
                itemElement.remove();
                // Check if cart is now empty
                const remainingItems = this.itemsContainer.querySelectorAll('.cart-drawer__item');
                if (remainingItems.length === 0) {
                  this.itemsContainer.innerHTML = this.getEmptyStateHTML();
                  this.setFooterVisibility(false);
                }
              }
            })

          }

          this.updateFooter(updatedCart);

          // Dispatch event to update cart count
          document.dispatchEvent(new CustomEvent('cart:change'));
        } catch (error) {
          console.error('Error removing cart item:', error);
          // Only refresh on error
          await this.refreshCart();
        } finally {
          button.disabled = false;
        }
      });
    });
  }

  open = async () => {
    if (this.isOpen) return;

    this.isOpen = true;
    this.drawer.style.visibility = 'visible';
    this.drawer.style.pointerEvents = 'auto';
    document.body.style.overflow = 'hidden';

    // Get current cart data
    try {
      const cartData = await cart.get();

      // Check if we have server-rendered items (count items in DOM)
      const renderedItems = this.itemsContainer?.querySelectorAll('.cart-drawer__item').length || 0;
      const actualItems = cartData.items?.length || 0;

      this.setFooterVisibility(actualItems > 0);

      // If item count doesn't match, we need to re-render
      // (happens when items are added after page load)
      if (renderedItems !== actualItems) {
        await this.refreshCart();
      } else {
        // Items match, just attach listeners on first open and update total
        if (!this.listenersAttached) {
          this.attachItemListeners();
          this.listenersAttached = true;
        }

        this.updateFooter(cartData);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }

    // Animate overlay
    gsap.to(this.overlay, {
      opacity: 1,
      duration: 0.3,
      ease: 'power2.out'
    });

    // Animate container (same pattern as search modal, but from right)
    gsap.to(this.drawerContainer, {
      x: '0%',
      opacity: 1,
      duration: 1,
      ease: 'power3.out'
    });
  }

  close = () => {
    if (!this.isOpen) return;

    this.isOpen = false;
    document.body.style.overflow = '';

    // Animate overlay out
    gsap.to(this.overlay, {
      opacity: 0,
      duration: 0.3,
      ease: 'power2.in'
    });

    // Animate container out (same pattern as search modal)
    gsap.to(this.drawerContainer, {
      x: '100%',
      opacity: 1,
      duration: .8,
      ease: 'cubic.in',
      onComplete: () => {
        this.drawer.style.visibility = 'hidden';
        this.drawer.style.pointerEvents = 'none';
      }
    });
  }
}

export default CartDrawer;

